import {
  type AttributionCartItem,
  type AttributionClick,
  type AttributionProductType,
  attributionRemoveExpiredItems,
  isAttributionCartItem,
  isAttributionClick,
  isRecord,
  isString,
  parseStorage,
} from '../attribution'
import {
  browserStorageReadItem,
  browserStorageWriteItem,
} from '../browser-storage'

export const RECOMMENDATION_ATTRIBUTION_STORAGE_KEY =
  'recommendationWidgetAttribution'

const RECOMMENDATION_ATTRIBUTION_VERSION = 1

type RecommendationAttributionInteractionType = 'add-to-cart' | 'product-click'
export type RecommendationAttributionProductType = AttributionProductType

export type RecommendationAttributionClick = AttributionClick & {
  areaId: string
  interactionType: RecommendationAttributionInteractionType
  serviceId: string
}

export type RecommendationAttributionCartItem = AttributionCartItem & {
  areaId: string
  serviceId: string
}

export type RecommendationAttributionStorage = {
  cartItems: RecommendationAttributionCartItem[]
  clicks: RecommendationAttributionClick[]
  version: typeof RECOMMENDATION_ATTRIBUTION_VERSION
}

const createEmptyStorage = (): RecommendationAttributionStorage => ({
  cartItems: [],
  clicks: [],
  version: RECOMMENDATION_ATTRIBUTION_VERSION,
})

const isInteractionType = (
  value: unknown,
): value is RecommendationAttributionInteractionType =>
  value === 'add-to-cart' || value === 'product-click'

const isClick = (value: unknown): value is RecommendationAttributionClick => {
  if (!isRecord(value)) {
    return false
  }

  const { areaId, interactionType, serviceId } = value

  return (
    isAttributionClick(value) &&
    isString(areaId) &&
    isInteractionType(interactionType) &&
    isString(serviceId)
  )
}

const isCartItem = (
  value: unknown,
): value is RecommendationAttributionCartItem => {
  if (!isRecord(value)) {
    return false
  }

  const { areaId, serviceId } = value

  return isAttributionCartItem(value) && isString(areaId) && isString(serviceId)
}

const isStorage = (
  value: unknown,
): value is RecommendationAttributionStorage => {
  if (!isRecord(value)) {
    return false
  }

  return (
    value.version === RECOMMENDATION_ATTRIBUTION_VERSION &&
    Array.isArray(value.clicks) &&
    Array.isArray(value.cartItems) &&
    value.clicks.every(isClick) &&
    value.cartItems.every(isCartItem)
  )
}

/**
 * Persists attribution state without allowing unavailable or blocked local storage to interrupt the user flow.
 */
export const recommendationAttributionWrite = (
  storage: RecommendationAttributionStorage,
): void => {
  let serializedStorage: string

  try {
    serializedStorage = JSON.stringify(storage)
  } catch {
    return
  }

  browserStorageWriteItem(
    'localStorage',
    RECOMMENDATION_ATTRIBUTION_STORAGE_KEY,
    serializedStorage,
  )
}

const removeExpiredAttribution = (
  storage: RecommendationAttributionStorage,
): RecommendationAttributionStorage => {
  return {
    ...storage,
    cartItems: attributionRemoveExpiredItems(storage.cartItems),
    clicks: attributionRemoveExpiredItems(storage.clicks),
  }
}

/**
 * Reads and validates attribution state, returning an empty state and resetting malformed stored data when necessary.
 */
export const recommendationAttributionRead =
  (): RecommendationAttributionStorage => {
    const emptyStorage = createEmptyStorage()

    const rawStorage = browserStorageReadItem(
      'localStorage',
      RECOMMENDATION_ATTRIBUTION_STORAGE_KEY,
    )

    if (!rawStorage) {
      return emptyStorage
    }

    const parsedStorage = parseStorage(rawStorage)

    if (isStorage(parsedStorage)) {
      const storage = removeExpiredAttribution(parsedStorage)

      if (
        storage.cartItems.length !== parsedStorage.cartItems.length ||
        storage.clicks.length !== parsedStorage.clicks.length
      ) {
        recommendationAttributionWrite(storage)
      }

      return storage
    }

    recommendationAttributionWrite(emptyStorage)

    return emptyStorage
  }
